// =====================================================================
// Instanzkonfiguration (refactor/dual-target, 22.10.2026).
//
// Dasselbe Repo laeuft auf zwei Zielen (Combsol OS Firma, Core privat).
// Alle Unterschiede kommen aus Umgebungsvariablen; jeder Standardwert
// entspricht dem Verhalten von combsol.app vor der Umstellung, damit main
// ohne neue Variablen unveraendert laeuft.
//
// Nur oeffentliche Werte: Die Konfiguration wird dem Browser in die
// index.html eingebettet (window.__HUB_INSTANCE__). Secrets gehoeren nie
// hierher. Uebersicht aller Variablen: .env.example, docs/env-*.md.
// =====================================================================

import { normalizeAppKey } from "./app-catalog.js";

export type AuthProvider = "microsoft" | "google";

export interface InstanceConfig {
  /** PRODUCT_NAME, z. B. "COMBSOL/OS" oder "CORE". Teil nach "/" wird gedimmt. */
  productName: string;
  /** PRODUCT_SUBTITLE, Zeile unter dem Produktnamen. */
  productSubtitle: string;
  /** PRODUCT_LOGO_URL, Logo oben links (Pfad oder https URL). */
  logoUrl: string;
  /** true, wenn PRODUCT_LOGO_URL gesetzt ist; dann zeigt auch die Anmeldeseite dieses Logo. */
  customLogo: boolean;
  /** ORGANIZATION_NAME, Statusleiste. */
  organizationName: string;
  /** ORGANIZATION_UNIT, Statusleiste rechts. */
  organizationUnit: string;
  /** PRIVACY_CONTROLLER, Verantwortlicher auf der Datenschutzseite. */
  privacyController: string;
  /** PRIVACY_CONTACT_EMAIL, Kontakt auf der Datenschutzseite. */
  privacyContactEmail: string;
  /** HUB_PUBLIC_URL ohne Schraegstrich am Ende, z. B. "https://combsol.app". */
  publicUrl: string;
  /** AUTH_PROVIDER: "microsoft" (Standard) oder "google". */
  authProvider: AuthProvider;
  /** SATELLITES: Satelliten IDs in Anzeigereihenfolge; null = alle in Standardreihenfolge. */
  satellites: string[] | null;
}

export const DEFAULT_INSTANCE: InstanceConfig = {
  productName: "COMBSOL/OS",
  productSubtitle: "From solutions to results",
  logoUrl: "/icons/clean-glass/v1/hub/icon.svg",
  customLogo: false,
  organizationName: "Combsol GmbH",
  organizationUnit: "TGA Inbetriebnahme",
  privacyController: "Combsol GmbH, Heidenheim",
  privacyContactEmail: "datenschutz@combsol.com",
  publicUrl: "https://combsol.app",
  authProvider: "microsoft",
  satellites: null,
};

type Env = Record<string, string | undefined>;

function text(value: string | undefined, fallback: string, max = 120): string {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;
  return trimmed.slice(0, max);
}

/**
 * Vertrauenswürdige Produktprofil-Erkennung: nur die bereits beim Start
 * geparste Instanzkonfiguration entscheidet über Core, nie Hostname, Label,
 * Nutzerrolle oder eine App-Freigabe.
 */
export function isCoreInstance(config: Pick<InstanceConfig, "productName">): boolean {
  return config.productName.split("/")[0].trim().toUpperCase() === "CORE";
}

/** Bestehende Instanzkonfiguration bestimmt die Plattformidentität, nie der Hostname. */
function defaultLogoForProduct(productName: string): string {
  return isCoreInstance({ productName })
    ? "/icons/clean-glass/v1/core/icon.svg"
    : DEFAULT_INSTANCE.logoUrl;
}

/** HUB_PUBLIC_URL normalisieren: nur http(s), ohne Pfad und ohne Schraegstrich am Ende. */
export function parsePublicUrl(value: string | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) return DEFAULT_INSTANCE.publicUrl;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:" && url.protocol !== "http:") return DEFAULT_INSTANCE.publicUrl;
    return url.origin;
  } catch {
    return DEFAULT_INSTANCE.publicUrl;
  }
}

export function parseAuthProvider(value: string | undefined): AuthProvider {
  const normalized = value?.trim().toLowerCase();
  return normalized === "google" ? "google" : "microsoft";
}

/**
 * SATELLITES als JSON Liste (["money","scan"]) oder kommagetrennt
 * ("money,scan"). IDs wie in client/src/lib/apps.ts (Kleinschreibung).
 * Nur fehlend/leer = null (Legacy-Standard). Gesetzte ungueltige oder
 * explizit leere Listen bleiben geschlossen: [] aktiviert keine App.
 */
export function parseSatelliteList(value: string | undefined): string[] | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  let items: unknown[];
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) return [];
      items = parsed;
    } catch {
      return [];
    }
  } else {
    items = trimmed.split(",");
  }
  const ids: string[] = [];
  for (const item of items) {
    if (typeof item !== "string") continue;
    // `fair` war eine reine UI-ID. Instanz- und Serverfilter arbeiten stets
    // mit dem stabilen App-Claim `fair-kapa`, tolerieren den alten Wert aber.
    const id = normalizeAppKey(item);
    if (/^[a-z0-9-]{1,40}$/.test(id) && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

export function parseInstanceEnv(env: Env): InstanceConfig {
  const logo = env.PRODUCT_LOGO_URL?.trim();
  const productName = text(env.PRODUCT_NAME, DEFAULT_INSTANCE.productName, 40);
  const isCore = isCoreInstance({ productName });
  return {
    productName,
    productSubtitle: text(env.PRODUCT_SUBTITLE, DEFAULT_INSTANCE.productSubtitle),
    logoUrl: logo ? logo.slice(0, 500) : defaultLogoForProduct(productName),
    customLogo: Boolean(logo),
    // Die bestehende Produktkonfiguration bestimmt die private Branding-
    // Voreinstellung, nie Hostname, Rolle oder App-Freigabe.
    organizationName: text(env.ORGANIZATION_NAME, isCore ? "Privat" : DEFAULT_INSTANCE.organizationName),
    organizationUnit: text(env.ORGANIZATION_UNIT, isCore ? "" : DEFAULT_INSTANCE.organizationUnit),
    privacyController: text(env.PRIVACY_CONTROLLER, DEFAULT_INSTANCE.privacyController, 200),
    privacyContactEmail: text(env.PRIVACY_CONTACT_EMAIL, DEFAULT_INSTANCE.privacyContactEmail),
    publicUrl: parsePublicUrl(env.HUB_PUBLIC_URL),
    authProvider: parseAuthProvider(env.AUTH_PROVIDER),
    satellites: parseSatelliteList(env.SATELLITES),
  };
}

/** Markenwort vor dem Schraegstrich: "COMBSOL/OS" → "COMBSOL", "CORE" → "CORE". */
export function brandWord(config: Pick<InstanceConfig, "productName">): string {
  return config.productName.split("/")[0].trim() || config.productName;
}

/** "COMBSOL" → "Combsol" fuer Fenstertitel. */
export function brandTitle(config: Pick<InstanceConfig, "productName">): string {
  const word = brandWord(config);
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Ordnet eine Liste nach SATELLITES. Ohne Konfiguration bleibt die Liste
 * unveraendert (heutiges Verhalten). Eintraege, die keine Satelliten sind
 * (isSatellite false), bleiben immer und behalten ihren Platz am Anfang.
 */
export function orderBySatellites<T>(
  items: readonly T[],
  satellites: readonly string[] | null,
  getId: (item: T) => string,
  isSatellite: (item: T) => boolean = () => true,
): T[] {
  if (!satellites) return [...items];
  const normalizedSatellites = satellites.map(normalizeAppKey);
  const fixed = items.filter((item) => !isSatellite(item));
  const chosen = items
    .filter((item) => isSatellite(item) && normalizedSatellites.includes(normalizeAppKey(getId(item))))
    .sort((a, b) => normalizedSatellites.indexOf(normalizeAppKey(getId(a))) - normalizedSatellites.indexOf(normalizeAppKey(getId(b))));
  return [...fixed, ...chosen];
}
